import { useState } from "react";

import { Divider, Icon } from "@blueprintjs/core";

import { Box, Flex } from "@/components/layout/flexbox";
import { ClickableText } from "@/components/pieces/ClickableText";
import { FadeInBox } from "@/components/pieces/FadeInBox";
import { IconCircle } from "@/components/pieces/IconCircle";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import {
  DARK_BLUE,
  DARK_GREEN,
  LIGHT_BLUE,
  LIGHT_GREEN,
  MIDTEXT_COLOR,
  SUBTEXT_COLOR,
} from "@/css/constants";
import { FeedbackExample } from "@/types/evaluation";

interface QuoteProps {
  quote?: string;
  isSuggested?: boolean;
}

export function Quote({ quote, isSuggested }: QuoteProps) {
  if (!quote || quote === "") {
    return null;
  }

  return (
    <Flex
      sx={{
        gap: "8px",
      }}
    >
      <IconCircle
        size={24}
        icon={isSuggested ? "lightbulb" : "citation"}
        backgroundColor={isSuggested ? LIGHT_GREEN : LIGHT_BLUE}
        color={isSuggested ? DARK_GREEN : DARK_BLUE}
        iconSize={12}
      />

      <Box
        bg={isSuggested ? LIGHT_GREEN : LIGHT_BLUE}
        p={12}
        sx={{ borderRadius: "10px", borderTopLeftRadius: 0 }}
      >
        <Box color={isSuggested ? DARK_GREEN : DARK_BLUE}>{quote}</Box>
      </Box>
    </Flex>
  );
}

interface EvaluationFeedbackExampleCardProps {
  feedbackExample: FeedbackExample;
}

function EvaluationFeedbackExampleCard({
  feedbackExample,
}: EvaluationFeedbackExampleCardProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { quote, feedback, suggestionQuote } = feedbackExample;

  return (
    <Flex
      flexDirection="column"
      height="100%"
      style={{
        gap: "16px",
      }}
    >
      <Quote quote={quote} />
      <Box>{feedback}</Box>

      {suggestionQuote && (
        <>
          <Clickable onClick={() => setIsOpen((prev) => !prev)}>
            <Flex
              alignItems="center"
              style={{ display: "inline-flex" }}
              width="fit-content"
              color={MIDTEXT_COLOR}
              fontSize={14}
            >
              <Box mr={1}>What might I have said?</Box>
              <Icon size={14} icon={isOpen ? "chevron-up" : "chevron-down"} />
            </Flex>
          </Clickable>
          {isOpen && (
            <FadeInBox css={{ overflow: "hidden" }} width={"100%"}>
              <Quote quote={suggestionQuote} isSuggested />
            </FadeInBox>
          )}
        </>
      )}
    </Flex>
  );
}

interface EvaluationFeedbackExamplesProps {
  feedbackExamples: FeedbackExample[];
}

export function EvaluationFeedbackExamples({
  feedbackExamples,
}: EvaluationFeedbackExamplesProps) {
  const [currentExampleIndex, setCurrentExampleIndex] = useState<number>(0);
  const numberOfExamples = feedbackExamples?.length ?? 0;

  const currentExample = feedbackExamples?.[currentExampleIndex];

  if (!currentExample) return null;
  return (
    <Box>
      <Flex
        pb={20}
        style={{
          gap: "8px",
        }}
        fontSize={14}
        alignItems="center"
        color={MIDTEXT_COLOR}
      >
        <Box fontWeight={600}>Examples</Box>
        <Box fontSize={12} color={SUBTEXT_COLOR}>
          {currentExampleIndex + 1} of {numberOfExamples}
        </Box>
        <Box flex={1} style={{ placeContent: "center" }}>
          <Divider />
        </Box>
        <Flex
          style={{
            gap: "16px",
          }}
          fontSize={12}
        >
          <ClickableText
            disabled={currentExampleIndex === 0}
            onClick={() => {
              setCurrentExampleIndex(
                (prev) => (prev - 1 + numberOfExamples) % numberOfExamples,
              );
            }}
            text="Previous"
          />
          <ClickableText
            disabled={currentExampleIndex === numberOfExamples - 1}
            onClick={() => {
              setCurrentExampleIndex((prev) => (prev + 1) % numberOfExamples);
            }}
            text="Next"
          />
        </Flex>
      </Flex>
      <EvaluationFeedbackExampleCard feedbackExample={currentExample} />
    </Box>
  );
}
